import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Plus, Loader2, Trash2, Pencil, Check, ShieldAlert, UploadCloud, X, GraduationCap, Film, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  fetchCursosAdmin, subirArchivoAcademia, crearCurso, actualizarCurso, eliminarCurso, toggleEstadoCurso,
} from "../lib/academia";

const SIMBOLO = { PEN: "S/", MXN: "$", COP: "$" };
const VACIO = {
  titulo: "", descripcion: "", categoria: "", precio: "", moneda: "PEN", nivel: "", instructor: "", instructor_cargo: "",
  imagen_path: "", destacado: false, activo: true, modulos: [],
  cert_nombre_curso: "", cert_horas: "", cert_resumen: "", ceo_cargo: "D. G.  CEO CONSTRULINK",
  cip_logo_path: "", firma_ceo_path: "", firma_instructor_path: "",
};
const CEO_NOMBRE = "Ing. Ever Santisteban Dominguez";
const LECCION_VACIA = { titulo: "", video_path: "", video_nombre: "", video_url: "", duracion: "", gratis: false };

export default function AcademiaAdmin() {
  const { user } = useAuth();
  const [vista, setVista] = useState("lista");
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [cursos, setCursos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [up, setUp] = useState({}); // clave -> {subiendo, progreso}

  function cargar() {
    setCargando(true);
    fetchCursosAdmin().then(setCursos).catch(() => {}).finally(() => setCargando(false));
  }
  useEffect(() => { cargar(); }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // ----- módulos / lecciones -----
  const addModulo = () => setForm((s) => ({ ...s, modulos: [...s.modulos, { titulo: "", lecciones: [] }] }));
  const removeModulo = (mi) => setForm((s) => ({ ...s, modulos: s.modulos.filter((_, i) => i !== mi) }));
  const setModTitulo = (mi, v) => setForm((s) => ({ ...s, modulos: s.modulos.map((m, i) => i === mi ? { ...m, titulo: v } : m) }));
  const addLeccion = (mi) => setForm((s) => ({ ...s, modulos: s.modulos.map((m, i) => i === mi ? { ...m, lecciones: [...m.lecciones, { ...LECCION_VACIA }] } : m) }));
  const removeLeccion = (mi, li) => setForm((s) => ({ ...s, modulos: s.modulos.map((m, i) => i === mi ? { ...m, lecciones: m.lecciones.filter((_, j) => j !== li) } : m) }));
  const setLec = (mi, li, k, v) => setForm((s) => ({
    ...s, modulos: s.modulos.map((m, i) => i === mi ? { ...m, lecciones: m.lecciones.map((l, j) => j === li ? { ...l, [k]: v } : l) } : m),
  }));

  async function subir(clave, file, onDone) {
    setUp((s) => ({ ...s, [clave]: { subiendo: true, progreso: 0 } }));
    try {
      const res = await subirArchivoAcademia(file, (pg) => setUp((s) => ({ ...s, [clave]: { subiendo: true, progreso: pg } })));
      onDone(res);
      toast.success("Archivo subido.");
    } catch {
      toast.error("No se pudo subir (revisa el tamaño máximo 200 MB).");
    } finally {
      setUp((s) => ({ ...s, [clave]: { subiendo: false, progreso: 0 } }));
    }
  }

  function nuevo() { setEditId(null); setForm(VACIO); setVista("form"); }
  function editar(c) {
    setEditId(c.id);
    setForm({
      titulo: c.titulo || "", descripcion: c.descripcion || "", categoria: c.categoria || "",
      precio: String(c.precio ?? ""), moneda: c.moneda || "PEN", nivel: c.nivel || "", instructor: c.instructor || "", instructor_cargo: c.instructor_cargo || "",
      imagen_path: c.imagen_path || "", destacado: !!c.destacado, activo: c.activo !== false,
      cert_nombre_curso: c.cert_nombre_curso || "", cert_horas: c.cert_horas || "", cert_resumen: c.cert_resumen || "",
      firma_ceo_path: c.firma_ceo_path || "", firma_instructor_path: c.firma_instructor_path || "",
      modulos: (c.modulos || []).map((m) => ({
        titulo: m.titulo || "",
        lecciones: (m.lecciones || []).map((l) => ({
          titulo: l.titulo || "", video_path: l.video_path || "", video_nombre: l.video_nombre || "",
          video_url: l.video_url || "", duracion: l.duracion || "", gratis: !!l.gratis, lid: l.lid,
        })),
      })),
    });
    setVista("form");
  }

  async function guardar(e) {
    e.preventDefault();
    if (!form.titulo.trim()) { toast.error("Ingresa el título del curso."); return; }
    if (form.precio === "" || isNaN(parseFloat(form.precio))) { toast.error("Ingresa un precio válido."); return; }
    setGuardando(true);
    const payload = {
      titulo: form.titulo.trim(), descripcion: form.descripcion, categoria: form.categoria,
      precio: parseFloat(form.precio), moneda: form.moneda, nivel: form.nivel || null, instructor: form.instructor || null,
      instructor_cargo: form.instructor_cargo || null,
      imagen_path: form.imagen_path || null, destacado: form.destacado, activo: form.activo,
      cert_nombre_curso: form.cert_nombre_curso || null, cert_horas: form.cert_horas || null, cert_resumen: form.cert_resumen || null,
      ceo_cargo: form.ceo_cargo || null, cip_logo_path: form.cip_logo_path || null,
      firma_ceo_path: form.firma_ceo_path || null, firma_instructor_path: form.firma_instructor_path || null,
      modulos: form.modulos.map((m) => ({
        titulo: m.titulo,
        lecciones: m.lecciones.map((l) => ({
          titulo: l.titulo, video_path: l.video_path || null, video_nombre: l.video_nombre || null,
          video_url: l.video_url || null, duracion: l.duracion || null, gratis: !!l.gratis, lid: l.lid || null,
        })),
      })),
    };
    try {
      if (editId) { await actualizarCurso(editId, payload); toast.success("Curso actualizado."); }
      else { await crearCurso(payload); toast.success("Curso creado."); }
      setVista("lista"); setEditId(null); setForm(VACIO); cargar();
    } catch { toast.error("No se pudo guardar el curso."); }
    finally { setGuardando(false); }
  }

  async function borrar(id) {
    if (!window.confirm("¿Eliminar este curso?")) return;
    await eliminarCurso(id).catch(() => {});
    setCursos((c) => c.filter((x) => x.id !== id));
    toast.success("Curso eliminado.");
  }
  async function toggle(id) {
    const r = await toggleEstadoCurso(id).catch(() => null);
    if (r) setCursos((c) => c.map((x) => (x.id === id ? { ...x, activo: r.activo } : x)));
  }

  if (user && user.role !== "admin") {
    return (
      <div className="max-w-lg mx-auto text-center py-20" data-testid="academia-admin-denegado">
        <ShieldAlert size={40} className="mx-auto text-brand-red" />
        <h2 className="mt-4 text-xl font-bold text-slate-900">Acceso restringido</h2>
        <Link to="/" className="mt-4 inline-block text-brand-blue font-semibold hover:underline">Volver al inicio</Link>
      </div>
    );
  }

  // ---------------- Formulario ----------------
  if (vista === "form") {
    return (
      <div className="px-5 md:px-8 py-8 max-w-4xl mx-auto" data-testid="academia-form">
        <button onClick={() => setVista("lista")} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="acad-form-volver">
          <ChevronLeft size={16} /> Volver a cursos
        </button>
        <h1 className="mt-3 text-2xl font-extrabold text-slate-900 tracking-tight">{editId ? "Editar curso" : "Nuevo curso"}</h1>

        <form onSubmit={guardar} className="mt-6 bg-white rounded-2xl border border-slate-100 shadow-soft p-6 md:p-8 space-y-7">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Campo label="Título"><input data-testid="c-titulo" value={form.titulo} onChange={(e) => set("titulo", e.target.value)} className="cl-input" placeholder="Metrados y Presupuestos" /></Campo>
            <Campo label="Categoría"><input data-testid="c-cat" value={form.categoria} onChange={(e) => set("categoria", e.target.value)} className="cl-input" placeholder="Curso online" /></Campo>
            <Campo label="Precio (PEN)"><input data-testid="c-precio" type="number" step="0.01" min="0" value={form.precio} onChange={(e) => set("precio", e.target.value)} className="cl-input" placeholder="0" /></Campo>
            <Campo label="Nivel"><input data-testid="c-nivel" value={form.nivel} onChange={(e) => set("nivel", e.target.value)} className="cl-input" placeholder="Básico / Intermedio / Avanzado" /></Campo>
            <Campo label="Miniatura (opcional)">
              <UploadBox id="c-imagen" accept="image/*" hint="JPG/PNG/WEBP" nombre={form.imagen_path ? "Imagen cargada" : ""}
                estado={up.imagen} onFile={(f) => subir("imagen", f, (r) => set("imagen_path", r.archivo_path))}
                onClear={() => set("imagen_path", "")} />
            </Campo>
          </div>
          <Campo label="Descripción"><textarea data-testid="c-desc" value={form.descripcion} onChange={(e) => set("descripcion", e.target.value)} rows={3} className="cl-input resize-none" placeholder="¿Qué aprenderá el alumno?" /></Campo>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 cursor-pointer">
              <input type="checkbox" data-testid="c-activo" checked={form.activo} onChange={(e) => set("activo", e.target.checked)} className="w-4 h-4 accent-brand-blue" /> Activo (visible en la tienda)
            </label>
            <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 cursor-pointer">
              <input type="checkbox" data-testid="c-destacado" checked={form.destacado} onChange={(e) => set("destacado", e.target.checked)} className="w-4 h-4 accent-brand-blue" /> Mostrar en destacados
            </label>
          </div>

          {/* Temario: módulos y lecciones */}
          <div className="pt-5 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-brand-orange" style={{ color: "#EA580C" }}>Temario · Módulos y lecciones</p>
              <button type="button" onClick={addModulo} data-testid="add-modulo" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:underline">
                <PlusCircle size={15} /> Añadir módulo
              </button>
            </div>

            {form.modulos.length === 0 && <p className="mt-3 text-sm text-slate-400">Aún no hay módulos. Añade el primero.</p>}

            <div className="mt-4 space-y-5">
              {form.modulos.map((m, mi) => (
                <div key={mi} className="rounded-xl border border-slate-200 p-4" data-testid={`modulo-${mi}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400">MÓDULO {mi + 1}</span>
                    <input data-testid={`modulo-titulo-${mi}`} value={m.titulo} onChange={(e) => setModTitulo(mi, e.target.value)} className="cl-input flex-1" placeholder="Título del módulo" />
                    <button type="button" onClick={() => removeModulo(mi)} data-testid={`del-modulo-${mi}`} className="p-2 text-brand-red hover:bg-red-50 rounded-lg"><Trash2 size={15} /></button>
                  </div>

                  <div className="mt-3 space-y-3 pl-2 border-l-2 border-slate-100">
                    {m.lecciones.map((l, li) => (
                      <div key={li} className="rounded-lg bg-slate-50 p-3" data-testid={`leccion-${mi}-${li}`}>
                        <div className="flex items-center gap-2">
                          <Film size={14} className="text-slate-400 flex-shrink-0" />
                          <input data-testid={`leccion-titulo-${mi}-${li}`} value={l.titulo} onChange={(e) => setLec(mi, li, "titulo", e.target.value)} className="cl-input flex-1" placeholder="Título de la lección" />
                          <input value={l.duracion} onChange={(e) => setLec(mi, li, "duracion", e.target.value)} className="cl-input w-24" placeholder="12:30" />
                          <button type="button" onClick={() => removeLeccion(mi, li)} className="p-2 text-brand-red hover:bg-red-100 rounded-lg"><X size={14} /></button>
                        </div>
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                          <div className="flex-1 min-w-[220px]">
                            <UploadBox id={`leccion-video-${mi}-${li}`} accept="video/*" hint="Video de la clase (máx. 200 MB)"
                              nombre={l.video_nombre} estado={up[`vid-${mi}-${li}`]}
                              onFile={(f) => subir(`vid-${mi}-${li}`, f, (r) => { setLec(mi, li, "video_path", r.archivo_path); setLec(mi, li, "video_nombre", r.archivo_nombre); })}
                              onClear={() => { setLec(mi, li, "video_path", ""); setLec(mi, li, "video_nombre", ""); }} />
                            <input data-testid={`leccion-video-url-${mi}-${li}`} value={l.video_url} onChange={(e) => setLec(mi, li, "video_url", e.target.value)} className="cl-input mt-2 text-xs" placeholder="… o pega enlace externo (Vimeo, Drive, S3, YouTube)" />
                          </div>
                          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
                            <input type="checkbox" data-testid={`leccion-gratis-${mi}-${li}`} checked={l.gratis} onChange={(e) => setLec(mi, li, "gratis", e.target.checked)} className="w-4 h-4 accent-emerald-600" /> Vista previa gratis
                          </label>
                        </div>
                      </div>
                    ))}
                    <button type="button" onClick={() => addLeccion(mi)} data-testid={`add-leccion-${mi}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-brand-blue">
                      <Plus size={14} /> Añadir lección
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Certificado de marca */}
          <div className="pt-5 border-t border-slate-100">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em]" style={{ color: "#EA580C" }}>Certificado de aprobación</p>
            <p className="mt-1 text-xs text-slate-500">Estos datos se imprimen en el diploma que el alumno descarga al completar el 100% del curso.</p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Campo label="Nombre del curso en el diploma"><input data-testid="c-cert-nombre" value={form.cert_nombre_curso} onChange={(e) => set("cert_nombre_curso", e.target.value)} className="cl-input" placeholder="(por defecto usa el título del curso)" /></Campo>
              <Campo label="Horas académicas"><input data-testid="c-cert-horas" value={form.cert_horas} onChange={(e) => set("cert_horas", e.target.value)} className="cl-input" placeholder="Ej: 40" /></Campo>
            </div>
            <div className="mt-4">
              <Campo label="Resumen del curso aprendido"><textarea data-testid="c-cert-resumen" value={form.cert_resumen} onChange={(e) => set("cert_resumen", e.target.value)} rows={2} className="cl-input resize-none" placeholder="Breve resumen de lo aprendido que aparecerá en el certificado." /></Campo>
            </div>

            {/* Logo institucional CIP */}
            <div className="mt-4">
              <Campo label="Logo institucional (CIP) — único para la plataforma, editable por curso">
                <UploadBox id="c-cip-logo" accept="image/*" hint="Logo del Colegio de Ingenieros del Perú (PNG con fondo transparente recomendado). Aparece en el sello del panel azul."
                  nombre={form.cip_logo_path ? "Logo CIP cargado" : ""} estado={up.ciplogo}
                  onFile={(f) => subir("ciplogo", f, (r) => set("cip_logo_path", r.archivo_path))}
                  onClear={() => set("cip_logo_path", "")} />
              </Campo>
            </div>

            {/* Firma CEO (nombre fijo, cargo editable) */}
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Firma · CEO</p>
              <div className="mt-2 flex items-center gap-2 text-sm font-bold text-slate-800" data-testid="c-ceo-nombre-fijo">
                <GraduationCap size={15} className="text-brand-blue" /> {CEO_NOMBRE}
                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 bg-slate-200 px-1.5 py-0.5 rounded">Nombre fijo</span>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-5">
                <Campo label="Cargo del CEO (editable)"><input data-testid="c-ceo-cargo" value={form.ceo_cargo} onChange={(e) => set("ceo_cargo", e.target.value)} className="cl-input" placeholder="D. G.  CEO CONSTRULINK" /></Campo>
                <Campo label="Imagen de la firma del CEO">
                  <UploadBox id="c-firma-ceo" accept="image/*" hint="Imagen PNG/JPG de la firma (fondo claro o transparente)"
                    nombre={form.firma_ceo_path ? "Firma cargada" : ""} estado={up.firmaceo}
                    onFile={(f) => subir("firmaceo", f, (r) => set("firma_ceo_path", r.archivo_path))}
                    onClear={() => set("firma_ceo_path", "")} />
                </Campo>
              </div>
            </div>

            {/* Firma Instructor (nombre + especialidad editables) */}
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Firma · Instructor</p>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-5">
                <Campo label="Nombre del instructor"><input data-testid="c-inst-nombre" value={form.instructor} onChange={(e) => set("instructor", e.target.value)} className="cl-input" placeholder="Nombre del instructor" /></Campo>
                <Campo label="Especialidad / cargo del instructor"><input data-testid="c-inst-especialidad" value={form.instructor_cargo} onChange={(e) => set("instructor_cargo", e.target.value)} className="cl-input" placeholder="Ing. Civil · Especialista en costos" /></Campo>
              </div>
              <div className="mt-4">
                <Campo label="Imagen de la firma del instructor">
                  <UploadBox id="c-firma-instructor" accept="image/*" hint="Imagen PNG/JPG de la firma del instructor"
                    nombre={form.firma_instructor_path ? "Firma cargada" : ""} estado={up.firmainst}
                    onFile={(f) => subir("firmainst", f, (r) => set("firma_instructor_path", r.archivo_path))}
                    onClear={() => set("firma_instructor_path", "")} />
                </Campo>
              </div>
            </div>
          </div>

          <button type="submit" disabled={guardando} data-testid="academia-guardar"
            className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-blue disabled:opacity-50 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
            {guardando ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} {editId ? "Guardar cambios" : "Crear curso"}
          </button>
        </form>
      </div>
    );
  }

  // ---------------- Tabla ----------------
  return (
    <div className="px-5 md:px-8 py-8 max-w-5xl mx-auto" data-testid="academia-admin">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver al Panel Admin
      </Link>
      <div className="mt-4 mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "#EA580C" }}>Pilar 03 · Academia</p>
          <h1 className="mt-1 text-3xl font-extrabold text-slate-900 tracking-tight">Cursos</h1>
          <p className="mt-1 text-slate-500 text-sm">Gestiona cursos, módulos y lecciones. Los descuentos Pro/Elite se aplican solos.</p>
        </div>
        <button onClick={nuevo} data-testid="nuevo-curso-btn"
          className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-blue text-white font-semibold px-5 py-2.5 rounded-xl transition-colors">
          <Plus size={17} /> Nuevo curso
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden">
        {cargando ? (
          <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>
        ) : cursos.length === 0 ? (
          <div className="p-12 text-center text-slate-400" data-testid="academia-vacio">Aún no hay cursos. Crea el primero con "Nuevo curso".</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="academia-tabla">
              <thead>
                <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="text-left px-5 py-3.5">Curso</th>
                  <th className="text-left px-4 py-3.5">Categoría</th>
                  <th className="text-left px-4 py-3.5">Precio</th>
                  <th className="text-left px-4 py-3.5">Contenido</th>
                  <th className="text-left px-4 py-3.5">Estado</th>
                  <th className="text-right px-5 py-3.5">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cursos.map((c) => (
                  <tr key={c.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60" data-testid={`academia-row-${c.id}`}>
                    <td className="px-5 py-4 font-semibold text-slate-900 max-w-xs">{c.titulo}{c.destacado && <span className="ml-2 text-[10px] font-bold uppercase text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Destacado</span>}</td>
                    <td className="px-4 py-4 text-slate-500">{c.categoria || "—"}</td>
                    <td className="px-4 py-4 font-bold text-slate-900 whitespace-nowrap">{SIMBOLO[c.moneda] || ""} {Number(c.precio).toFixed(2)}</td>
                    <td className="px-4 py-4 text-slate-500 whitespace-nowrap">{c.num_modulos} mód · {c.num_lecciones} lec</td>
                    <td className="px-4 py-4">
                      <button onClick={() => toggle(c.id)} data-testid={`academia-toggle-${c.id}`} className={`text-xs font-bold uppercase tracking-wide ${c.activo ? "text-emerald-600" : "text-slate-400"}`}>
                        {c.activo ? "Activo" : "Oculto"}
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => editar(c)} title="Editar" data-testid={`academia-editar-${c.id}`} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-brand-blue"><Pencil size={16} /></button>
                        <button onClick={() => borrar(c.id)} title="Eliminar" data-testid={`academia-borrar-${c.id}`} className="p-2 rounded-lg text-brand-red hover:bg-red-50"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function UploadBox({ id, accept, hint, nombre, estado, onFile, onClear }) {
  return (
    <div>
      <input id={id} type="file" accept={accept} className="hidden" data-testid={id}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
      {nombre ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
          <span className="flex items-center gap-2 text-emerald-700 truncate"><Check size={14} /> {nombre}</span>
          <button type="button" onClick={onClear} className="text-slate-400 hover:text-brand-red flex-shrink-0"><X size={14} /></button>
        </div>
      ) : (
        <label htmlFor={id} className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-500 cursor-pointer hover:border-brand-blue hover:text-brand-blue transition-colors">
          {estado?.subiendo ? <><Loader2 size={14} className="animate-spin" /> Subiendo {estado.progreso}%</> : <><UploadCloud size={14} /> Seleccionar</>}
        </label>
      )}
      {hint && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

const Campo = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
